import { useCallback, useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { Button, IconButton, Typography, Box, Tooltip } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import ResponsiveTabs from "@nualang/nualang-ui-components/dist/Navigation/ResponsiveTabs";

import ClassroomList from "@nualang/nualang-ui-components/dist/Lists/Classrooms";
import ClassroomsNotFound from "@nualang/nualang-ui-components/dist/Misc/NotFound/Classrooms";
import UpgradeSubscription from "@nualang/nualang-ui-components/dist/Dialogs/UpgradeSubscription/UpgradeSubscription";
import ClassroomsToolbar from "./ClassroomsToolbar";
import stickyBarBleed from "../stickyBarBleed";
import { classrooms as classroomHelpers } from "../../../utils/helpers";

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
    padding: theme.spacing(1, 2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2, 3),
    },
  },
  actions: {
    marginBottom: theme.spacing(1),
  },
  header: {
    display: "flex",
    alignItems: "center",
  },
  headerText: {
    flexGrow: 1,
  },
  title: {
    fontWeight: "bold",
    fontSize: "1.2em",
    marginBottom: theme.spacing(2),
  },
}));

function Classrooms({
  t = (text) => text,
  role,
  learnClassrooms = [],
  teachClassrooms = [],
  archivedClassrooms = [],
  isLoading,
  placeholderImageUrl,
  singleLineGrid = false,
  singleLineGridCols = 4,
  handleJoinClassroom,
  handleLeaveClassroom,
  handleViewClassroom,
  handleSearchClassrooms,
  membersProps,
  getMemberDetails,
  mainSiteUrl,
  memberPlaceholderImageUrl,
  getClassroomMember,
  getClassroomMembers,
  username,
  subscription = {},
  handleViewUserProfile,
  featureFlags,
}) {
  console.log("Classrooms render")
  const { classes } = useStyles();
  const [isUpgradeSubscriptionOpen, setIsUpgradeSubscriptionOpen] =
    useState(false);
  const handleCloseUpgradeSubcriptionModal = () => {
    setIsUpgradeSubscriptionOpen(false);
  };
  const handleSubscriptionPage = () => {
    window.open(`${mainSiteUrl}/pricing/app`, "_blank");
  };

  const [filters, setFilters] = useState(classroomHelpers.DEFAULT_FILTERS);

  const handleVisibilityChange = useCallback((event) => {
    setFilters((prev) => ({ ...prev, visibility: event.target.value }));
  }, []);
  const handleLangChange = useCallback((event) => {
    setFilters((prev) => ({ ...prev, lang: event.target.value }));
  }, []);
  const handleSortChange = useCallback((event) => {
    setFilters((prev) => ({ ...prev, sort: event.target.value }));
  }, []);
  const handleSearchChange = useCallback((value) => {
    setFilters((prev) => ({ ...prev, search: value }));
  }, []);
  const handleClearFilters = useCallback(
    () => setFilters(classroomHelpers.DEFAULT_FILTERS),
    [],
  );
  const filtersAreDefault = useMemo(
    () => classroomHelpers.isDefaultFilters(filters),
    [filters],
  );

  const activeClassrooms = useMemo(() => {
    const seen = new Set();
    return [...teachClassrooms, ...learnClassrooms].filter((classroom) => {
      if (!classroom?.classroomId || seen.has(classroom.classroomId)) {
        return false;
      }
      seen.add(classroom.classroomId);
      return true;
    });
  }, [teachClassrooms, learnClassrooms]);
  const filteredActiveClassrooms = useMemo(
    () => classroomHelpers.filterAndSortClassrooms(activeClassrooms, filters),
    [activeClassrooms, filters],
  );
  const filteredLearnClassrooms = useMemo(
    () => classroomHelpers.filterAndSortClassrooms(learnClassrooms, filters),
    [learnClassrooms, filters],
  );

  const tabs = [
    {
      label: `${t("active")} ${isLoading ? "" : `(${activeClassrooms.length})`}`,
      id: "active",
    },
    {
      label: `${t("archived")} ${isLoading ? "" : `(${archivedClassrooms.length})`}`,
      id: "archived",
    },
  ];

  let initialTabValue = 0;

  if (window.location.hash) {
    const selectedTab = window.location.hash;
    initialTabValue = tabs.findIndex(
      (tab) => tab.id === selectedTab.replace("#", ""),
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headerText}>
          <Typography variant='h5' component='h1'>
            {t(`classrooms`)}
          </Typography>
        </div>
        {handleSearchClassrooms && (
          <Tooltip title={t("find_classroom")}>
            <IconButton
              onClick={handleSearchClassrooms}
              aria-label={t("find_classroom")}
              color='inherit'
              size='large'
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>
        )}
      </div>
      {role === "teach" ? (
        <ResponsiveTabs
          t={t}
          tabs={tabs}
          centered
          sticky
          stickySx={stickyBarBleed}
          initialValue={initialTabValue}
        >
          <Box sx={{
            py: 1
          }}>
            {!isLoading && activeClassrooms.length === 0 ? (
              <Box sx={{
                py: 2
              }}>
                <ClassroomsNotFound
                  t={t}
                  placeholderImageUrl={placeholderImageUrl}
                  title={
                    <Typography className={classes.title}>
                      {t("teach_classrooms_title")}
                    </Typography>
                  }
                  description={t("teach_classrooms_desc")}
                  handleSearchClassrooms={handleSearchClassrooms}
                  mainSiteUrl={mainSiteUrl}
                />
              </Box>
            ) : (
              <>
                {activeClassrooms.length > 0 && (
                  <ClassroomsToolbar
                    t={t}
                    visibility={filters.visibility}
                    onVisibilityChange={handleVisibilityChange}
                    lang={filters.lang}
                    onLangChange={handleLangChange}
                    sort={filters.sort}
                    onSortChange={handleSortChange}
                    search={filters.search}
                    onSearchChange={handleSearchChange}
                    onClearFilters={handleClearFilters}
                    clearFiltersDisabled={filtersAreDefault}
                  />
                )}
                {activeClassrooms.length > 0 &&
                filteredActiveClassrooms.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: "center" }}>
                    <Typography>{t("no_results_found")}</Typography>
                    <Button
                      onClick={handleClearFilters}
                      disabled={filtersAreDefault}
                      sx={{ mt: 1 }}
                    >
                      {t("clear_filters")}
                    </Button>
                  </Box>
                ) : (
                  <ClassroomList
                    t={t}
                    memberPlaceholderImageUrl={memberPlaceholderImageUrl}
                    classrooms={
                      activeClassrooms.length ? filteredActiveClassrooms : null
                    }
                    handleJoinClassroom={handleJoinClassroom}
                    handleLeaveClassroom={handleLeaveClassroom}
                    handleViewClassroom={handleViewClassroom}
                    membersProps={membersProps}
                    getMemberDetails={getMemberDetails}
                    singleLineGrid={singleLineGrid}
                    singleLineGridCols={singleLineGridCols}
                    getClassroomMember={getClassroomMember}
                    getClassroomMembers={getClassroomMembers}
                    username={username}
                    handleViewUserProfile={handleViewUserProfile}
                    cardTitleComponent='h2'
                    featureFlags={featureFlags}
                  />
                )}
              </>
            )}
          </Box>
          <Box sx={{
            py: 1
          }}>
            {isLoading || archivedClassrooms.length > 0 ? (
              <ClassroomList
                t={t}
                memberPlaceholderImageUrl={memberPlaceholderImageUrl}
                classrooms={
                  Array.isArray(archivedClassrooms) && archivedClassrooms.length
                    ? archivedClassrooms
                    : null
                }
                handleJoinClassroom={handleJoinClassroom}
                handleLeaveClassroom={handleLeaveClassroom}
                handleViewClassroom={handleViewClassroom}
                membersProps={membersProps}
                getMemberDetails={getMemberDetails}
                singleLineGrid={singleLineGrid}
                singleLineGridCols={singleLineGridCols}
                getClassroomMember={getClassroomMember}
                getClassroomMembers={getClassroomMembers}
                username={username}
                handleViewUserProfile={handleViewUserProfile}
                cardTitleComponent='h2'
                featureFlags={featureFlags}
              />
            ) : (
              <Box sx={{
                py: 2
              }}>
                <ClassroomsNotFound
                  t={t}
                  placeholderImageUrl={placeholderImageUrl}
                  title={
                    <Typography className={classes.title}>
                      {t("archived_classrooms_title")}
                    </Typography>
                  }
                  description={t("archived_classrooms_desc")}
                  handleCreateClassroom={null}
                  subscription={subscription}
                />
              </Box>
            )}
          </Box>
        </ResponsiveTabs>
      ) : (
        <Box sx={{
          py: 1
        }}>
          {!isLoading && learnClassrooms.length === 0 ? (
            <Box sx={{
              py: 2
            }}>
              <ClassroomsNotFound
                t={t}
                placeholderImageUrl={placeholderImageUrl}
                title={
                  <Typography className={classes.title}>
                    {t("learn_classrooms_title")}
                  </Typography>
                }
                description={t("learn_classrooms_desc")}
                handleSearchClassrooms={handleSearchClassrooms}
                mainSiteUrl={mainSiteUrl}
              />
            </Box>
          ) : (
            <>
              {learnClassrooms.length > 0 && (
                <ClassroomsToolbar
                  t={t}
                  visibility={filters.visibility}
                  onVisibilityChange={handleVisibilityChange}
                  lang={filters.lang}
                  onLangChange={handleLangChange}
                  sort={filters.sort}
                  onSortChange={handleSortChange}
                  search={filters.search}
                  onSearchChange={handleSearchChange}
                  onClearFilters={handleClearFilters}
                  clearFiltersDisabled={filtersAreDefault}
                  showVisibilityFilter={false}
                />
              )}
              {learnClassrooms.length > 0 &&
              filteredLearnClassrooms.length === 0 ? (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography>{t("no_results_found")}</Typography>
                  <Button
                    onClick={handleClearFilters}
                    disabled={filtersAreDefault}
                    sx={{ mt: 1 }}
                  >
                    {t("clear_filters")}
                  </Button>
                </Box>
              ) : (
                <ClassroomList
                  t={t}
                  memberPlaceholderImageUrl={memberPlaceholderImageUrl}
                  classrooms={
                    learnClassrooms.length ? filteredLearnClassrooms : null
                  }
                  handleJoinClassroom={handleJoinClassroom}
                  handleLeaveClassroom={handleLeaveClassroom}
                  handleViewClassroom={handleViewClassroom}
                  membersProps={membersProps}
                  getMemberDetails={getMemberDetails}
                  singleLineGrid={singleLineGrid}
                  singleLineGridCols={singleLineGridCols}
                  getClassroomMember={getClassroomMember}
                  getClassroomMembers={getClassroomMembers}
                  username={username}
                  handleViewUserProfile={handleViewUserProfile}
                  cardTitleComponent='h2'
                  featureFlags={featureFlags}
                />
              )}
            </>
          )}
        </Box>
      )}
      <UpgradeSubscription
        key={"UpgradeSubscription"}
        t={t}
        open={isUpgradeSubscriptionOpen}
        handleClose={handleCloseUpgradeSubcriptionModal}
        onConfirm={handleSubscriptionPage}
        subscription={subscription}
      />
    </div>
  );
}

export default Classrooms;
